import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormFTB3809 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Complete Form FTB 3509 Easily: With Line-by-Line Filing Guide"
          description="Learn about CA FTB Form 3509 for reporting political activities by Section 23701d organizations and ensure compliance with California state regulations."
          keywords=""
          canonical="resources/tax-exempt/form-ftb-3509-guide/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container
        maxWidth="lg"
        sx={{ textAlign: 'center', justifyContent: 'center' }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 3,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight">FTB 3509 </span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Political or Legislative Activities
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=frb3509`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-ftb-3509-guide/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form FTB 3509 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form FTB 3509? Form FTB 3509{' '}
                        </strong>{' '}
                        is a California form used by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          R&TC Section 23701d{' '}
                        </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          tax-exempt organizations
                        </Link>{' '}
                        to report{' '}
                        <strong style={{ fontWeight: 600 }}>
                          political and legislative activities{' '}
                        </strong>
                        conducted during the taxable year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?{' '}
                        </strong>{' '}
                        A{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          23701d tax-exempt organization{' '}
                        </strong>
                        generally files FTB 3509 when it engages in{' '}
                        <strong style={{ fontWeight: 600 }}>
                          political or legislative activities,
                        </strong>{' '}
                        such as supporting or opposing candidates or attempting
                        to influence legislation.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        The form reports information about the organization's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          political activities, legislative activities,
                          expenditures, and related activities{' '}
                        </strong>
                        during the year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          California Filing Connection:{' '}
                        </strong>{' '}
                        FTB 3509 is a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          California tax-exempt organization reporting
                          requirement
                        </strong>{' '}
                        and should not be confused with the federal{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-c/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990 Schedule C{' '}
                        </Link>
                        , which covers similar categories of political and
                        lobbying activity under federal rules .
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form FTB 3509?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form FTB 3509?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 3509 with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sources" sx={{ color: '#0202ff' }}>
                          Source & References
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is Form 3509?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <strong style={{ fontWeight: 600 }}>
                    purpose of form FTB 3509
                  </strong>{' '}
                  is to declare political or legislative activities carried out
                  by R&TC Section 23701d tax-exempt organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These organizations must report any political or legislative
                  activities they engage in during the year. Political
                  activities include supporting or opposing candidates for
                  public office, while legislative activities pertain to
                  attempts to influence legislation.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Form FTB 3509?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Organizations that are classified under Section 23701d as
                  tax-exempt charities or social welfare groups must file Form
                  3509 if they engage in political or legislative activities,
                  during the taxable year such as
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    Participated or intervened in any political campaign on
                    behalf of any elective public office.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    {' '}
                    Contributed funds to support or oppose any individual public
                    office candidate or any organizations formed to support or
                    oppose a public office candidate.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Attempted to influence any national, state, or local
                    legislation or ballot measure.
                  </li>
                </ul>

                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone for a filing experience that goes
                        beyond expectations.
                      </Typography>
                      <Typography variant="body2">
                        Empower your tax compliance journey with us today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=ftb3509`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to file Form 3509 with TaxZerone
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftb-f3509-political-img.webp" />
                  <source srcSet="/assets/images/ftb-f3509-political-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftb-f3509-political-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3509 Political or Legislative activities by section 23701d-Organization details"
                    width={760}
                    height={198}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  For calendar year 2025 or fiscal year beginning and ending
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the beginning and end dates of the tax year if it
                  follows a fiscal year instead of the calendar year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Corporation/Organization name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the legal name of the nonprofit organization filing the
                  form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  California corporation number
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the registration number assigned by the California
                  Secretary of State.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Street address (suite, room, or PMB no.)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the organization’s full street address, including suite
                  or unit number if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  FEIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the organization’s Federal Employer Identification
                  Number issued by the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  City, State, ZIP code
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Fill in the city, state, and ZIP code for the organization’s
                  mailing address.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part I-Political Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftb-f3509-part1-img.webp" />
                  <source srcSet="/assets/images/ftb-f3509-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftb-f3509-part1-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3509 Part I-Political Activities, Organization supported or opposed a candidate Line 1 and Line 2"
                    width={760}
                    height={245}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate whether the organization took part in or influenced
                    any political campaign for an elected public office
                    candidate.{' '}
                  </li>
                  <li>
                    If “Yes,” briefly describe the activities and provide a
                    summary of any materials that were published related to
                    those activities. If the organization has not taken part in
                    any political campaign, check “No”.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate whether the organization gave money to support or
                    oppose a public office candidate, or to any organization
                    created for that purpose.
                  </li>
                  <li>
                    If “Yes,” briefly describe the activity and include the name
                    of the person or organization that received the
                    contribution, along with the amount paid and the date of the
                    contribution. If not, check “No”.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Part II-Legislative Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/ftb-f3509-part2-img.webp" />
                  <source srcSet="/assets/images/ftb-f3509-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftb-f3509-part2-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3509 Part II-Legislative Activities for organization attempted to influence legislation Line 3 to Line 4"
                    width={760}
                    height={332}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Check “Yes,” if the organization attempted to influence any
                    national, state, or local legislation, or a ballot measure,
                    and did not file federal Form 5768.
                  </li>
                  <li>
                    If your organization did not attempt to influence any
                    legislation at all, then check “No”.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 4a
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate whether the organization filed federal Form 5768
                    during the 2025 tax year. If “Yes,” attach a copy of the
                    form filed with the IRS and skip Question 4b, as this also
                    serves as the election for state purposes.
                  </li>
                  <li>
                    If “No,” proceed to Question 4b and refer to the
                    instructions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether the organization filed federal Form 5768 in a
                  previous year and has not revoked that election.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <strong style={{ fontWeight: 600 }}>Note:</strong> This
                  election cannot be made if the organization is a church, an
                  integrated auxiliary of a church, a private foundation, or an
                  affiliated organization.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/ftb-f3509-line5-7-img.webp" />
                  <source srcSet="/assets/images/ftb-f3509-line5-7-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/ftb-f3509-line5-7-img.png"
                    loading="lazy"
                    alt="CA FTB Form 3509 Part II-Legislative Activities for organization attempted to influence legislation Line 5 to Line 7"
                    width={760}
                    height={176}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount paid or incurred to support the
                  organization’s exempt activities like charitable, education,
                  religious, etc.,
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount spent to influence legislation through
                  direct communication with legislative bodies, government
                  officials or employees who may participate in the formation of
                  legislation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Grassroots expenditures are the money your organization
                    spends to influence a law by shaping public opinion.
                  </li>
                  <li>
                    Enter the total money spent to influence legislation by
                    persuading or mobilizing the public.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                  id="sources"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on official California{' '}
                        <Link
                          href="https://www.ftb.ca.gov/forms/2025/2025-3509-instructions.html"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          FTB
                        </Link>{' '}
                        Form 3509 . For complete and updated information, refer
                        to the official California Franchise Tax Board website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" id="common" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is considered political or legislative activity?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 0 }}>
                      Political or legislative activity refers to any action
                      that is intended to influence the selection, nomination,
                      election, or appointment of a public official or to
                      support or oppose legislation.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What happens if my organization doesn&apos;t file Form
                  3509?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 0, mt: 1 }}>
                      Failure to file Form 3509 could result in penalties or
                      loss of tax-exempt status if the organization has
                      conducted reportable political or legislative activities.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Does every non-profit organization need to file Form 3509?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      No, only organizations that have engaged in political or
                      legislative activities are required to file Form 3509.
                    </Typography>

                    <Typography variant="body1">
                      By providing clear guidance and offering easy filing
                      solutions, TaxZerone ensures your organization stays
                      compliant with all reporting requirements.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default FormFTB3809;
